<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div class="max-dashboard">
      <p v-if="!specId" class="text-muted">{{ _('Choose a dashboard in the component settings.') }}</p>
      <p v-else-if="loading && !item" class="text-muted"><i class="fa fa-fw fa-cog fa-spin"></i> {{ _('Loading...') }}</p>
      <p v-else-if="loadingFailed" class="text-muted">{{ _('This item is no longer available.') }}</p>
      <div v-else-if="item && blockedReason" class="alert alert-info">{{ blockedReason }}</div>
      <MaxDocument v-else-if="doc" :doc="doc" :labels="maxLabels">
        <template #actions>
          <label class="sr-only" :for="periodSelectId">{{ _('Period') }}</label>
          <select :id="periodSelectId" class="form-control input-sm max-dashboard__period" :value="period" v-on:change="setPeriod($event.target.value)">
            <option v-for="(label, value) in maxPeriodLabels" :key="value" :value="value">{{ label }}</option>
          </select>
          <button type="button" class="max-action" :disabled="refreshing" :aria-label="_('Refresh')" :title="_('Refresh')" v-on:click="refresh(true)">
            <i class="fa fa-fw fa-rotate" :class="{ 'fa-spin': refreshing }" aria-hidden="true"></i>
          </button>
        </template>
        <template #notice>
          <ul class="max-dashboard__freshness" aria-live="polite">
            <li v-for="row in freshness" :key="row.id" :data-state="row.state">
              <i class="fa fa-fw" :class="row.icon" aria-hidden="true"></i>
              <span>{{ row.name }}</span>
              <span class="text-muted">{{ row.text }}</span>
            </li>
          </ul>
        </template>
      </MaxDocument>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import components from '~/lib/components'
import maxlabels from '~/lib/maxlabels'
import maxspecs from '~/lib/maxspecs'
import reporttypes from '~/lib/reporttypes'
import { materializeSpec, liveRequest, liveOutput, withPeriod, sourceMaxAge, PERIODS } from '#max-core'

// Shared across dashboards so two panels asking for the same query within its age share one request
const liveCache = new Map()
const TICK_MS = 60000
const POLL_MS = 3000
const POLL_TRIES = 100

export default {
  data () {
    return {
      item: null,
      period: null,
      live: {},
      liveState: {},
      snapshots: {},
      running: false,
      runFailed: false,
      runAttempted: {},
      requestId: null,
      pollGeneration: 0,
      now: Date.now()
    }
  },
  mixins: [components, maxlabels, maxspecs, reporttypes],
  computed: {
    config () {
      if (this.component && this.component.initData && this.component.initData.specId) {
        return this.component.initData
      }
      try {
        return this.component && this.component.data ? JSON.parse(this.component.data) : {}
      } catch (e) {
        return {}
      }
    },
    specId () {
      return parseInt(this.config.specId, 10) || null
    },
    spec () {
      return this.item && this.item.valid ? this.item.spec : null
    },
    viewSpec () {
      return this.spec && this.period ? withPeriod(this.spec, this.period) : null
    },
    periodSelectId () {
      return 'max-dashboard-period-' + (this.component && this.component.id ? this.component.id : this.specId)
    },
    blockedReason () {
      if (!this.item) {
        return ''
      }
      if (this.item.kind !== 'dashboard') {
        return this._('This definition is a report, not a dashboard.')
      }
      if (!this.item.valid) {
        return this._('The stored definition is no longer valid and will not run:') + ' ' + (this.item.issues[0] ? this.item.issues[0].message : '')
      }
      if (!this.item.approved || this.item.status !== 'active') {
        return this._('This dashboard must be approved and active before it can be shown.')
      }
      if (this.item.analysis && this.userLevel < this.item.analysis.viewLevel) {
        return this._('Only shop administrators can view this dashboard.')
      }
      return ''
    },
    sourceEntries () {
      return this.viewSpec ? Object.entries(this.viewSpec.sources || {}) : []
    },
    refreshing () {
      return this.running || Object.values(this.liveState).some(state => state === 'loading')
    },
    doc () {
      if (!this.viewSpec || this.blockedReason) {
        return null
      }
      const data = {}
      for (const [id, source] of this.sourceEntries) {
        if (source.live) {
          if (this.live[id] !== undefined) {
            data[id] = this.live[id]
          }
        } else if (this.snapshots[id]) {
          data[id] = this.snapshots[id].data
        }
      }
      return materializeSpec(this.viewSpec, data, {
        now: new Date(this.now),
        messages: {
          noData: this._('No data for this period.'),
          partFailed: this._('This part of the report could not be displayed.')
        }
      })
    },
    freshness () {
      return this.sourceEntries.map(([id, source]) => {
        const name = this.sourceName(source)
        if (source.live) {
          const state = this.liveState[id]
          if (state === 'loading') {
            return { id, name, state: 'loading', icon: 'fa-cog fa-spin', text: this._('Updating...') }
          }
          if (state === 'failed') {
            return { id, name, state: 'failed', icon: 'fa-triangle-exclamation', text: this._('Could not be updated') }
          }
          const cached = liveCache.get(this.liveKey(source))
          return { id, name, state: 'fresh', icon: 'fa-bolt', text: cached ? this._('Updated %s').replace('%s', this.timeLabel(cached.at)) : '' }
        }
        const snapshot = this.snapshots[id]
        if (this.running) {
          return { id, name, state: 'loading', icon: 'fa-cog fa-spin', text: snapshot ? this._('Updating... Showing data from %s').replace('%s', this.timeLabel(snapshot.generated_ts * 1000)) : this._('Updating...') }
        }
        if (!snapshot) {
          return { id, name, state: 'failed', icon: 'fa-triangle-exclamation', text: this.runFailed ? this._('Could not be updated') : this._('No data yet') }
        }
        const stale = this.isStale(source, snapshot)
        return { id, name, state: stale ? 'stale' : 'fresh', icon: stale ? 'fa-clock' : 'fa-circle-check', text: this._('Updated %s').replace('%s', this.timeLabel(snapshot.generated_ts * 1000)) }
      })
    }
  },
  watch: {
    specId (id) {
      if (id && (!this.item || this.item.id !== id)) {
        this.item = null
        this.load()
      }
    }
  },
  methods: {
    timeLabel (ms) {
      return this.$filters.timestampToDate(Math.floor(ms / 1000))
    },
    liveKey (source) {
      const request = liveRequest(this.viewSpec, source)
      return request ? this.shopId + '|' + request.path + '?' + new URLSearchParams(request.query).toString() : ''
    },
    isStale (source, snapshot) {
      return !snapshot || this.now - snapshot.generated_ts * 1000 > sourceMaxAge(source) * 60000
    },
    initialPeriod () {
      const wanted = this.config.period || (this.spec && this.spec.period) || 'yesterday'
      return PERIODS.includes(wanted) ? wanted : 'yesterday'
    },
    async load () {
      this.loading = true
      this.loadingFailed = false
      const period = this.period || (this.config.period && PERIODS.includes(this.config.period) ? this.config.period : '')
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/maxspecs/' + this.specId + '.json?snapshots=1&period=' + encodeURIComponent(period) + '&apikey=' + this.apiKey)
      } catch (e) {
        this.loading = false
        this.loadingFailed = true
        this.item = null
        return
      }
      this.loading = false
      this.item = response.data
      if (!this.period) {
        this.period = this.initialPeriod()
      }
      this.snapshots = response.data.snapshot_period === this.period ? (response.data.snapshots || {}) : {}
      if (response.data.snapshot_period !== this.period) {
        await this.loadSnapshots()
      }
      this.refresh(false)
    },
    async loadSnapshots () {
      if (!this.item) {
        return
      }
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/maxspecs/' + this.item.id + '.json?snapshots=1&period=' + encodeURIComponent(this.period) + '&apikey=' + this.apiKey)
        if (response.data.spec_hash !== this.item.spec_hash) {
          this.item = response.data
        }
        if (response.data.snapshot_period === this.period) {
          this.snapshots = response.data.snapshots || {}
        }
      } catch (e) {}
    },
    setPeriod (period) {
      if (!PERIODS.includes(period) || period === this.period) {
        return
      }
      this.period = period
      this.live = {}
      this.liveState = {}
      this.snapshots = {}
      this.runFailed = false
      this.pollGeneration++
      this.running = false
      this.loadSnapshots().then(() => this.refresh(false))
    },
    refresh (force) {
      if (!this.viewSpec || this.blockedReason) {
        return
      }
      this.now = Date.now()
      if (force) {
        this.runAttempted = {}
      }
      for (const [id, source] of this.sourceEntries) {
        if (source.live) {
          this.fetchLive(id, source, force)
        }
      }
      const staleSnapshots = this.sourceEntries.some(([id, source]) => !source.live && this.isStale(source, this.snapshots[id]))
      const runKey = this.period + '|' + this.item.spec_hash
      if (staleSnapshots && !this.running && !this.runAttempted[runKey]) {
        this.runAttempted = { ...this.runAttempted, [runKey]: true }
        this.runSnapshots()
      }
    },
    async fetchLive (id, source, force) {
      const request = liveRequest(this.viewSpec, source)
      if (!request) {
        return
      }
      const key = this.liveKey(source)
      const cached = liveCache.get(key)
      if (!force && cached && Date.now() - cached.at < sourceMaxAge(source) * 60000) {
        this.live = { ...this.live, [id]: cached.data }
        this.liveState = { ...this.liveState, [id]: 'fresh' }
        return
      }
      if (this.liveState[id] === 'loading') {
        return
      }
      const period = this.period
      this.liveState = { ...this.liveState, [id]: 'loading' }
      try {
        const query = new URLSearchParams({ ...request.query, apikey: this.apiKey }).toString()
        const response = await axios.get(this.baseDomain + this.shopId + '/' + request.path + '?' + query)
        const data = liveOutput(response.data)
        liveCache.set(key, { at: Date.now(), data })
        if (period !== this.period) {
          return
        }
        this.live = { ...this.live, [id]: data }
        this.liveState = { ...this.liveState, [id]: 'fresh' }
      } catch (e) {
        if (period === this.period) {
          this.liveState = { ...this.liveState, [id]: 'failed' }
        }
      }
    },
    makeRequestId () {
      const alphabet = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
      let id = ''
      for (let i = 0; i < 8; i++) {
        id += alphabet.charAt(Math.floor(Math.random() * alphabet.length))
      }
      return id
    },
    async runSnapshots () {
      const requestId = this.makeRequestId()
      const generation = ++this.pollGeneration
      this.requestId = requestId
      this.running = true
      this.runFailed = false
      let response
      try {
        response = await axios.post(this.baseDomain + this.shopId + '/scheduledreports.json?apikey=' + this.apiKey, {
          requestid: requestId,
          type: 'max',
          time: 'now',
          spec_id: this.item.id,
          period: this.period
        })
      } catch (e) {
        if (generation === this.pollGeneration) {
          this.running = false
          this.runFailed = true
        }
        return
      }
      if (response.data && response.data.fresh) {
        return this.finishRun(generation)
      }
      for (let i = 0; i < POLL_TRIES; i++) {
        await new Promise(resolve => setTimeout(resolve, POLL_MS))
        if (generation !== this.pollGeneration || !this.running) {
          return
        }
        try {
          const status = await axios.get(this.baseDomain + this.shopId + '/jobstatus/' + requestId + '.json?apikey=' + this.apiKey)
          const rows = Array.isArray(status.data) ? status.data : (status.data ? [status.data] : [])
          if (rows.find(r => r.status === 'done' || r.status === 'fail')) {
            return this.finishRun(generation)
          }
        } catch (e) {}
      }
      if (generation === this.pollGeneration) {
        this.running = false
      }
    },
    async finishRun (generation) {
      if (generation !== this.pollGeneration || !this.running) {
        return
      }
      await this.loadSnapshots()
      if (generation !== this.pollGeneration) {
        return
      }
      this.running = false
      this.now = Date.now()
      this.runFailed = this.sourceEntries.some(([id, source]) => !source.live && !this.snapshots[id])
    },
    tick () {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return
      }
      this.refresh(false)
    }
  },
  created () {
    if (this.specId) {
      this.load()
    }
  },
  mounted () {
    if (!this.events) {
      this.events = {
        taskUpdate: (message) => {
          if (!message || !message.data || !this.requestId || message.data.requestid !== this.requestId) {
            return
          }
          if (message.data.status === 'done' || message.data.status === 'fail') {
            this.finishRun(this.pollGeneration)
          }
        },
        maxSpecUpdate: (message) => {
          if (message && this.item && message.id === this.item.id) {
            this.load()
          }
        }
      }
    }
    this.enableEventBus()
    this.timer = setInterval(() => this.tick(), TICK_MS)
  },
  beforeUnmount () {
    this.pollGeneration++
    clearInterval(this.timer)
  }
}
</script>

<style scoped>
.max-dashboard__period {
  width: auto;
  display: inline-block;
}
.max-dashboard__freshness {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  list-style: none;
  padding: 0;
  margin: 0 0 12px;
  font-size: 12px;
}
.max-dashboard__freshness li {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}
.max-dashboard__freshness [data-state="failed"] .fa,
.max-dashboard__freshness [data-state="stale"] .fa {
  color: #8a6d3b;
}
</style>
